/**
 * `model-correct` - a second, correcting read when the reading so far does not
 * hold together (production's correction pass): the problems found and the
 * first answer go in, and the model re-reads.
 *
 * Its candidates are added only when its reading is better by
 * `isBetterReading`, so a resolver that prefers it keeps the first reading
 * otherwise. Options: those of `model-read`, plus `of`, the model step whose
 * answer it corrects. Needs `answer:<of>`.
 */

import { normalizeExtraction } from '../../extraction.js';
import { isBetterReading, validateExtraction } from '../../validate.js';
import { parseAnswer } from '../prompt/asks.js';
import type { Step } from '../types.js';
import { readNeeds, readPrompt, readProvides, type ModelReadOptions } from './model-read.js';
import { currentReading, summarize, transportOf } from './support.js';

export interface ModelCorrectOptions extends ModelReadOptions {
  of: string;
}

export const modelCorrectStep: Step<ModelCorrectOptions> = {
  name: 'model-correct',
  needs: (options) => [...readNeeds(options), `answer:${options.of}`],
  provides: readProvides,
  async run(state, context) {
    const transport = transportOf(context.ports);
    const report = validateExtraction(normalizeExtraction(currentReading(state)));
    if (report.ok) return { skipped: 'the reading holds together' };
    const problems = report.issues.filter((issue) => issue.severity !== 'info').map((issue) => issue.message);
    const prompt = readPrompt(state, transport, context.options, { problems, previous: state.answers[context.options.of] });
    const response = await transport.send({ ...prompt, schemaName: 'receipt' });
    const tokens = { input: response.inputTokens, output: response.outputTokens };
    const corrected = validateExtraction(normalizeExtraction(response.raw));
    if (!isBetterReading(corrected, report)) {
      return { answer: response.raw, tokens, dropped: prompt.dropped, added: [`not better; kept the first reading (${problems.join(' ')})`] };
    }
    const candidates = parseAnswer(response.raw, context.id, context.options.ask);
    return { answer: response.raw, candidates, tokens, dropped: prompt.dropped, added: [`better (${problems.join(' ')})`, ...summarize(candidates)] };
  },
};
